/**
 * Build the visible-space op model WITHOUT corrupting the target.
 *
 * The old `buildVisibleTarget` re-injected existing deletions into the AI's `cleanTarget` at a
 * position guessed by a character diff of accepted→target. On a substantial rephrase that guess is
 * garbage — it split words mid-token (e.g. "each" → "eac(…)h") — and every downstream op cascaded
 * from the corrupted target.
 *
 * This module never touches the target. Instead:
 *  1. `acceptedBaseline` = the visible baseline with existing DELETION spans removed (the accepted
 *     state; existing insertions stay because they are part of accepted content).
 *  2. Diff `acceptedBaseline` → `cleanTarget` (both clean) → the genuine new edits, no corruption.
 *  3. `interleaveExistingDeletions` walks that diff back into visible/baseline coordinates, emitting
 *     each existing deletion as a `keep` at its EXACT known offset (so it stays struck, untouched),
 *     and splitting any new `keep`/`delete` run around those deletion spans.
 *
 * The result is a normal `Op[]` in baseline coordinates (what `locateOps` expects) with the hard
 * invariant preserved: `keep`+`delete` reconstruct the visible baseline exactly.
 */

import { Op, RevisionSpan } from './types';

interface Span {
  start: number;
  end: number;
}

/** The accepted state: `baselineVisible` with existing deletion spans removed. */
export function removeDeletionSpans(baselineVisible: string, deletionSpans: Span[]): string {
  const spans = [...deletionSpans].sort((a, b) => a.start - b.start);
  let accepted = '';
  let cursor = 0;
  for (const s of spans) {
    if (s.start < cursor) continue; // defensive against overlaps
    accepted += baselineVisible.slice(cursor, s.start);
    cursor = s.end;
  }
  accepted += baselineVisible.slice(cursor);
  return accepted;
}

/**
 * Drop the leading portion of a fresh `insert` that verbatim re-types text which is already struck
 * as an existing deletion immediately before it.
 *
 * The accepted-vs-target diff removes existing deletions from the baseline, so when the AI target
 * reproduces already-deleted wording (it happens when the model is shown the marked-up paragraph
 * and echoes our own struck text back), that wording has nowhere to align and is emitted as a brand
 * new `insert` right next to the preserved deletion. The result is the same phrase struck through
 * AND re-inserted as-is — pure redline noise. We keep the existing deletion struck (design intent,
 * see RevisionSpan docs) and strip only the redundant re-insertion, leaving any genuine new tail.
 *
 * Matching is token-aligned (whitespace-folded) and requires a token boundary after the match, so
 * `catalog` is never trimmed just because `cat` was deleted next to it. Returns the insert text with
 * the re-typed deletion prefix removed (possibly empty → the whole insert should be dropped).
 */
function stripReinsertedDeletionPrefix(insertValue: string, deletionText: string): string {
  const delTokens = deletionText.trim().split(/\s+/).filter(Boolean);
  if (delTokens.length === 0) return insertValue;

  let pos = 0;
  for (const tok of delTokens) {
    while (pos < insertValue.length && /\s/.test(insertValue[pos])) pos++;
    if (insertValue.slice(pos, pos + tok.length) !== tok) return insertValue; // not a re-insertion
    pos += tok.length;
  }
  // Require a token boundary (end-of-string or whitespace) so partial-word overlaps don't match.
  if (pos < insertValue.length && !/\s/.test(insertValue[pos])) return insertValue;
  while (pos < insertValue.length && /\s/.test(insertValue[pos])) pos++;
  return insertValue.slice(pos);
}

/**
 * Translate accepted-space ops back into visible/baseline coordinates, re-inserting existing
 * deletions as `keep` at their exact offsets. `deletionSpans` are in visible-baseline coordinates.
 */
export function interleaveExistingDeletions(
  acceptedOps: Op[],
  baselineVisible: string,
  deletionSpans: Span[],
): Op[] {
  const spans = [...deletionSpans].sort((a, b) => a.start - b.start);
  const out: Op[] = [];
  let v = 0; // visible cursor
  let di = 0; // index into spans

  // Emit any deletion spans that begin at (or before) the current visible cursor, as `keep`.
  // Returns the deletion text emitted by THIS call, so an immediately-following `insert` can detect
  // (and drop) a redundant re-typing of the text that was just struck.
  const flushDeletionsAt = (): string => {
    let flushed = '';
    while (di < spans.length && spans[di].start <= v) {
      const s = spans[di];
      if (s.end > v) {
        const value = baselineVisible.slice(v, s.end);
        out.push({ kind: 'keep', value });
        flushed += value;
        v = s.end;
      }
      di += 1;
    }
    return flushed;
  };

  // Consume `value` accepted characters as `kind`, splitting around any deletion spans in the way.
  const emitRun = (kind: 'keep' | 'delete', value: string): void => {
    let idx = 0;
    while (idx < value.length) {
      flushDeletionsAt();
      const nextDelStart = di < spans.length ? spans[di].start : Infinity;
      const take = Math.min(value.length - idx, nextDelStart - v); // >= 1 after the flush
      out.push({ kind, value: value.slice(idx, idx + take) });
      idx += take;
      v += take;
    }
  };

  for (const op of acceptedOps) {
    const flushedDeletion = flushDeletionsAt();
    if (op.kind === 'insert') {
      // If this insert merely re-types the deletion we just re-emitted (struck), drop that prefix so
      // the phrase is shown struck once, not struck AND re-inserted verbatim.
      const value = flushedDeletion
        ? stripReinsertedDeletionPrefix(op.value, flushedDeletion)
        : op.value;
      if (value) out.push({ kind: 'insert', value });
    } else {
      emitRun(op.kind, op.value);
    }
  }
  flushDeletionsAt(); // trailing deletion spans

  return coalesce(out);
}

/** Reconstruct the visible target (what the diff would compare against) from ops, for trace/debug. */
export function targetVisibleFromOps(ops: Op[]): string {
  return ops.filter((o) => o.kind !== 'delete').map((o) => o.value).join('');
}

/** Deletion spans from a revision context, in visible-baseline coordinates. */
export function deletionSpansOf(revisions: RevisionSpan[]): Span[] {
  return revisions.filter((r) => r.kind === 'deletion').map((r) => ({ start: r.start, end: r.end }));
}

/** Merge adjacent same-kind ops. */
function coalesce(ops: Op[]): Op[] {
  const merged: Op[] = [];
  for (const op of ops) {
    if (op.value === '') continue;
    const prev = merged[merged.length - 1];
    if (prev && prev.kind === op.kind) prev.value += op.value;
    else merged.push({ ...op });
  }
  return merged;
}
